import { Button, Col, Dropdown, Form, Input, Menu, notification, Row, Skeleton, Space } from "antd";
import { RequiredMark } from "antd/lib/form/Form";
import { Fragment, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { getEnvironmentService } from "../../backend";
import { currentProjectState } from "../../recoil/project";
import { Environment, EnvironmentDetails } from "../../types";
import { DownOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CreateStageRequestCloudProviderEnum, CreateStageRequestInfrastructureAsCodeToolEnum } from "@microtica/ms-engine-sdk";
import { trackEnvCreateSucceeded, trackEnvCreateFailed } from "../../backend/tracking/environment";
import { getEnvironmentsLimit } from "../../utils/local-storage";

interface EnvironmentSelectorProps {
    disabled?: boolean;
    selectedEnv?: EnvironmentDetails;
    onSelect?: (environment?: EnvironmentDetails) => void;
}

const EnvironmentSelect = ({
    disabled,
    selectedEnv,
    onSelect
}: EnvironmentSelectorProps) => {
    const [form] = Form.useForm();
    const [requiredMark] = useState<RequiredMark>('optional');
    const currentProject = useRecoilValue(currentProjectState);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [mode, setMode] = useState<"new" | "existing">("existing");
    const [enableCreateEnvironment, setEnableCreateEnvironment] = useState(true);
    const [loadingEnvs, setLoadingEnvs] = useState(true);
    const [creatingEnv, setCreatingEnv] = useState(false);

    const loadEnvironments = async () => {
        try {
            const { data: { stages: environments } } = await getEnvironmentService().getStages(currentProject!.id);
            const environmentsLimit = getEnvironmentsLimit();
            if (environmentsLimit <= environments.length) {
                setEnableCreateEnvironment(false);
            }
            const defaultEnv = environments.length === 1 ? environments[0] : undefined;

            if (!selectedEnv && defaultEnv) {
                onSelect?.({
                    id: defaultEnv.id,
                    name: defaultEnv.name,
                    accountId: defaultEnv.awsAccountId,
                    region: defaultEnv.awsRegion,
                    cloudProvider: defaultEnv.cloudProvider
                });
            }

            setEnvironments(environments.map(env => ({
                id: env.id,
                name: env.name,
                cloudProvider: env.cloudProvider,
                accountId: env.awsAccountId,
                region: env.awsRegion
            })));
            setMode(environments.length > 0 ? "existing" : "new");

        } catch (error) {

        } finally {
            setLoadingEnvs(false);
        }
    }

    useEffect(() => {
        loadEnvironments();
    }, []);

    useEffect(() => {
        if (mode === "new") {
            onSelect?.();
        } else {
            if (!selectedEnv && environments.length === 1) {
                // onSelect() must be explicitely called because the hook for selectedEnvironment is not triggered if environments[0] === selectedEnvironment
                onSelect?.(environments[0]);
            }
        }
    }, [mode]);

    const handleCreateEnvironment = async (values: { name: string, description: string }) => {
        try {
            setCreatingEnv(true);
            const { data: { id } } = await getEnvironmentService().createStage(
                currentProject!.id,
                {
                    name: values.name,
                    description: values.description,
                    cloudProvider: CreateStageRequestCloudProviderEnum.Aws,
                    infrastructureAsCodeTool: CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation
                });

            await loadEnvironments();
            setMode("existing");
            onSelect?.({ ...values, id, cloudProvider: CreateStageRequestCloudProviderEnum.Aws });
            notification.success({ message: "New environment created" });
            trackEnvCreateSucceeded(
                id,
                values.name,
                CreateStageRequestCloudProviderEnum.Aws,
                CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation,
                false
            );
        } catch (error: any) {
            notification.error({
                message: "Cannot create new environment",
                description: error.response.data.message
            });
            trackEnvCreateFailed(
                values.name,
                CreateStageRequestCloudProviderEnum.Aws,
                CreateStageRequestInfrastructureAsCodeToolEnum.Cloudformation,
                false,
                error.response.data.message
            );
        } finally {
            setCreatingEnv(false);
        }
    }

    const NewEnvironment = () => (
        <Form
            form={form}
            layout="vertical"
            initialValues={{}}
            onFinish={handleCreateEnvironment}
            requiredMark={requiredMark}
        >
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item
                        name="name"
                        label="Name"
                        required
                        rules={[{ required: true, message: 'Please input environment name!' }]}
                    >
                        <Input placeholder="development" disabled={disabled} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="description"
                        label="Description"
                        required
                        rules={[{ required: true, message: 'Please input environment description!' }]}
                    >
                        <Input placeholder="My development environment" maxLength={100} showCount disabled={disabled} />
                    </Form.Item>
                </Col>
            </Row>
            <br />
            <Row>
                <Col span={12} style={{ textAlign: "left" }}>
                    {
                        environments.length > 0 &&
                        <Button type="link" style={{ padding: 0 }} onClick={() => {
                            form.resetFields();
                            setMode("existing");
                        }}>
                            Choose existing environment
                        </Button>
                    }
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                    <Button type="primary" htmlType="submit" loading={creatingEnv} disabled={disabled}>
                        Create
                    </Button>
                </Col>
            </Row>
        </Form>
    );

    const ExistingEnvironment = () => (
        <>
            <Space>
                Select Environment:
                <Dropdown
                    trigger={["click"]}
                    placement="bottomRight"
                    overlay={
                        <Menu placeholder={selectedEnv?.id || "Select environment"}>
                            {
                                environments.map(env => (
                                    <Menu.Item key={env.id} onClick={() => onSelect?.(env)}>
                                        {env.name}
                                    </Menu.Item>
                                ))
                            }
                            {enableCreateEnvironment &&
                                <Fragment>
                                    <Menu.Divider />
                                    <Menu.Item key="create-environment" onClick={() => setMode("new")}>
                                        <PlusCircleOutlined /> Create Environment
                                    </Menu.Item>
                                </Fragment>
                            }
                        </Menu>
                    }
                    disabled={disabled}
                >
                    <Button>
                        {selectedEnv ? selectedEnv.name : "Select environment"} <DownOutlined />
                    </Button>
                </Dropdown>
            </Space>
        </>
    );

    return (
        loadingEnvs ? <Skeleton /> :
            mode == "new" ? <NewEnvironment /> : <ExistingEnvironment />
    );
};


export default EnvironmentSelect;